import PropTypes from 'prop-types';
import { createContext, useMemo } from 'react';
import useReportController, {
  formatRangeDates,
  getDefaultPastMonthRange,
} from '../hooks/useReportController';
import { getAffiliateReport } from '../services/reports';
import { DEFAULT_PRODUCT_CURRENCY_CODE, DOLARIZATION_SUPPORTED_CURRENCIES } from '../utils/currency';

export const ReportAffiliateContext = createContext({
  table: {},
  affiliateReport: [],
  fetchingAffiliate: false,
  countAffiliate: 0,
  currency: DEFAULT_PRODUCT_CURRENCY_CODE,
  setCurrency: () => {},
  currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
});

const ReportAffiliateProvider = ({ children }) => {
  const {
    table,
    data,
    fetching: fetchingAffiliate,
    dates,
    setDates,
    currency,
    setCurrency,
    currencyOptions,
    mutateExport,
    loadingExport,
  } = useReportController({
    report: 'affiliate',
    getDefaultDates: getDefaultPastMonthRange,
    formatDates: formatRangeDates,
    tableFilterOptions: { defaultCurrentPage: 1 },
    buildQueryKey: ({ page, dates: formattedDates, currency: apiCurrency }) => [
      'reports/affiliate',
      page,
      formattedDates,
      apiCurrency,
    ],
    fetchReport: ({ page, dates: formattedDates, currency: apiCurrency }) =>
      getAffiliateReport({ page, dates: formattedDates, currency: apiCurrency }),
    initialData: { results: [], count: 0 },
    buildExportParams: (extension) => ({ extension }),
    invalidateQueryKey: ['reports/affiliate'],
  });

  const { results: affiliateReport = [], count: countAffiliate } = data;

  const value = useMemo(
    () => ({
      affiliateReport,
      table,
      fetchingAffiliate,
      countAffiliate,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      mutateExport,
      loadingExport,
    }),
    [
      affiliateReport,
      table,
      fetchingAffiliate,
      countAffiliate,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      mutateExport,
      loadingExport,
    ]
  );

  return (
    <ReportAffiliateContext.Provider value={value}>{children}</ReportAffiliateContext.Provider>
  );
};

ReportAffiliateProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ReportAffiliateProvider;
